import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Save } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import Editor from "@/components/Editor";
import { sanitizeLetterHtml, stripHtmlTags } from "@/utils/htmlUtils";

export default function EditLetter() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [content, setContent] = useState("");
  const [deliveryDate, setDeliveryDate] = useState<Date | undefined>();
  const [contentError, setContentError] = useState(false);

  const { data: letter, isLoading } = useQuery({
    queryKey: ["letter-to-edit", id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("letters_with_owner")
        .select("content, delivery_date")
        .eq("id", id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (letter) {
      setContent(letter.content || "");
      if (letter.delivery_date) {
        setDeliveryDate(new Date(letter.delivery_date + "T00:00:00"));
      }
    }
  }, [letter]);

  const mutation = useMutation({
    mutationFn: async (updatedData: { content: string; delivery_date: string }) => {
      if (!id) throw new Error("ID da carta não encontrado");
      const { error } = await supabase
        .from("letters")
        .update(updatedData)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Carta atualizada com sucesso!" });
      queryClient.invalidateQueries({ queryKey: ["scheduled-letters"] });
      navigate("/admin/cartas-agendadas");
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Erro ao atualizar a carta",
        description: error.message,
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const safeContent = sanitizeLetterHtml(content);
    const isContentEmpty = stripHtmlTags(safeContent).trim().length === 0;
    setContentError(isContentEmpty);

    if (isContentEmpty || !deliveryDate) {
      toast({ variant: "destructive", title: "Preencha todos os campos" });
      return;
    }
    mutation.mutate({
      content: safeContent,
      delivery_date: format(deliveryDate, "yyyy-MM-dd"),
    });
  };

  if (isLoading) {
    return <div>Carregando carta...</div>;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900">Editar Carta Agendada</h1>
      <Card>
        <CardHeader>
          <CardTitle>Modifique o conteúdo ou a data de entrega</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label>Data de Entrega</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn("w-full justify-start text-left font-normal", !deliveryDate && "text-muted-foreground")}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {deliveryDate ? format(deliveryDate, "dd 'de' MMMM, yyyy", { locale: ptBR }) : <span>Selecione a data</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={deliveryDate}
                    onSelect={setDeliveryDate}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label htmlFor="content">Mensagem da Carta</Label>
              <Editor
                initialContent={content}
                onContentChange={(newContent) => {
                  setContent(newContent);
                  if (contentError) setContentError(false);
                }}
                hasError={contentError}
              />
            </div>
            <Button type="submit" disabled={mutation.isPending}>
              <Save className="mr-2 h-4 w-4" />
              {mutation.isPending ? "Salvando..." : "Salvar Alterações"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}